import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FormProvider,useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { ERollingOffersInternalViewModel } from '@appcharge/shared-ui';
import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import usePermissions from 'api/usePermissions';
import useTheme from 'api/useTheme';
import {
  EBadgeType,
  Offer,
  ProgressBarPoint,
  RollingOfferProductSequence
} from 'common/contracts';
import {
  MIN_ROLLING_SUB_OFFERS
} from 'constants/constants';
import { ERollingOfferFormState, OfferType } from 'constants/enums';
import { Rolling } from 'design-system/AcSidebar/constants';
import { useTabTracking } from 'hooks/useTabTracking';
import { errorResponse } from 'utils/errorsTextHelper';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormData } from 'views/Offers/hooks/useOfferFormData';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import {
  DEFAULT_OFFER_DESIGN_EXTERNAL_ID,
  ROLLING_OFFER_MODEL_BADGE_MAP
} from 'views/Offers/Offer.consts';
import {
  buildBaseOfferPayload,
  buildSchedulePayload
} from 'views/Offers/Offer.utils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import FormSectionsTabs from 'components/FormSectionsTabs';
import { Tabs, TabsContent } from 'components/ui/tabs';

import RollingOfferModals from './components/Sections/Modals';
import ConfigurationTab from './components/Tabs/ConfigurationTab';
import ScheduleTab from './components/Tabs/ScheduleTab';
import SettingsTab from './components/Tabs/SettingsTab';
import {
  ROLLING_OFFER_SCHEMA,
  RollingOfferFormFields
} from './RollingOfferForm.consts';
import { IRollingOfferFormProps } from './RollingOfferForm.types';
import {
  buildRollingOfferFormInitialValues,
  convertPriceFromCents,
  convertPriceToCents
} from './RollingOfferForm.utils';
import { RollingOfferPreview } from './RollingOfferPreview';

const RollingOfferForm: React.FC<IRollingOfferFormProps> = ({
  edit = false,
  dup = false
}): React.ReactElement => {
  const { publisherId, rollingOfferId } = useParams();
  const navigate = useNavigate();
  const { canUserEdit } = usePermissions();

  const { addOffer, updateOffer, getOffer } = useOffers({
    offerId: rollingOfferId,
    offerType: OfferType.ROLLING_OFFER
  });

  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Rolling Offer',
    addOffer,
    updateOffer,
    formatError: errorResponse
  });

  const [productsSequence, setProductsSequence] = useState<
    RollingOfferProductSequence[]
  >([]);
  const [barPointsValidity, setBarPointsValidity] = useState<
    Record<number, boolean>
  >({});
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [tab, setTab] = useState(ERollingOfferFormState.GENERAL);

  const {
    offerDesignOptions,
    defaultOfferDesignId,
    currentOfferUI,
    setCurrentOfferUI
  } = useOfferDesignOptions({
    publisherId: publisherId || '',
    offerDesignType: OfferDesignTypes.RollingOffer,
    defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.ROLLING_OFFER],
    offerId: rollingOfferId
  });

  const onDataLoaded = useCallback(
    (result: Partial<Offer>): void => {
      setCurrentOfferUI(
        result.offerUi?._id || result.offerUi?.offerUiId || ''
      );
      if (result.productsSequence) {
        setProductsSequence(
          (result.productsSequence as RollingOfferProductSequence[]).map(
            (item) => ({
              ...item,
              priceInUsdCents: convertPriceFromCents(item?.priceInUsdCents || 0)
            })
          )
        );
      }
    },
    [setCurrentOfferUI]
  );

  const { data, isLoading, chosenSegment, setChosenSegment } = useOfferFormData({
    offerId: rollingOfferId,
    publisherId,
    getOffer,
    onDataLoaded
  });

  const { getImages } = useImages(publisherId);
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);

  const relevantBadgeType: EBadgeType = useMemo(() => {
    const model = generalTheme.data
      ?.rollingOffersInternalViewModel as ERollingOffersInternalViewModel;
    return ROLLING_OFFER_MODEL_BADGE_MAP[model] ?? EBadgeType.EMBLEM;
  }, [generalTheme.data?.rollingOffersInternalViewModel]);

  const form = useForm<RollingOfferFormFields>({
    resolver: zodResolver(ROLLING_OFFER_SCHEMA),
    mode: 'onChange',
    defaultValues: buildRollingOfferFormInitialValues({
      data,
      edit,
      dup,
      defaultOfferDesignId,
      getOfferResult: getOffer?.data?.result
    })
  });

  const { formState, watch, handleSubmit, reset } = form;

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      reset(
        buildRollingOfferFormInitialValues({
          data,
          edit,
          dup,
          defaultOfferDesignId,
          getOfferResult: getOffer?.data?.result
        }),
        { keepDirty: false }
      );
    }
  }, [data, edit, dup, defaultOfferDesignId, getOffer?.data?.result, reset]);

  useEffect(() => {
    if (rollingOfferId) return;
    if (!productsSequence?.length) {
      const name = watch('name');
      const initialProductsSequence = Array.from(
        { length: MIN_ROLLING_SUB_OFFERS },
        (_, i) => ({
          index: i + 1,
          priceInUsdCents: 0,
          displayName: name,
          products: [
            {
              product: {
                images: []
              }
            }
          ]
        })
      );
      setProductsSequence(initialProductsSequence);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsSequence?.length, rollingOfferId]);

  const hasMissingProductDetails = useMemo((): boolean => {
    return productsSequence.some(
      ({ products }) =>
        !products.length ||
        products.some(
          ({ product, quantity }) => !product?.publisherProductId || !quantity
        )
    );
  }, [productsSequence]);

  const watchedOfferUiId = watch('offerUiId');
  useEffect(() => {
    if (!watchedOfferUiId) return;
    setCurrentOfferUI(watchedOfferUiId);
  }, [watchedOfferUiId, setCurrentOfferUI]);

  const rollingOfferTabs = useMemo(
    () => [
      { value: ERollingOfferFormState.GENERAL, label: 'Settings' },
      { value: ERollingOfferFormState.CONFIGURATION, label: 'Configuration' },
      { value: ERollingOfferFormState.SCHEDULE, label: 'Schedule' }
    ],
    []
  );

  const handleCustomNavigate = useCallback(
    (value: string): void => {
      setTab(value as ERollingOfferFormState);
    },
    []
  );

  const { handleTabChange } = useTabTracking({
    sidebarTab: Rolling.title,
    tabs: rollingOfferTabs,
    customNavigate: handleCustomNavigate
  });

  const onSubmit = useCallback(
    (values: RollingOfferFormFields): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newOffer: Record<string, any> = {
        ...buildBaseOfferPayload(values, chosenSegment, OfferType.ROLLING_OFFER),
        priority: values.priority,
        schedule: buildSchedulePayload(values.schedule),
        productsSequence: productsSequence.length
          ? productsSequence.map((seq) => {
              const progressBarPoints: ProgressBarPoint[] | undefined =
                seq.progressBarPoints?.[0]?.barId &&
                seq.progressBarPoints?.[0]?.points
                  ? [
                      {
                        points: seq.progressBarPoints?.[0]?.points,
                        barId: seq.progressBarPoints?.[0]?.barId
                      }
                    ]
                  : [];

              const hasValidProducts = seq.products.some(
                (product) => product?.product?.productId
              );

              let products: unknown[] = [];
              if (hasValidProducts) {
                products = seq.products.map((product) => ({
                  productId: product?.product?.productId,
                  quantity: product.quantity,
                  publisherProductId: product.product?.publisherProductId
                }));
              }

              const isFreeSubOffer =
                convertPriceToCents(seq?.priceInUsdCents || 0) === 0;

              return {
                index: seq.index || 1,
                products,
                displayName: seq.displayName || values.name,
                progressBarPoints,
                priceInUsdCents: convertPriceToCents(seq?.priceInUsdCents || 0),
                ...(!isFreeSubOffer && seq?.priceDiscount?.discount && {
                  priceDiscount: seq?.priceDiscount
                }),
                ...(!isFreeSubOffer && seq?.productSale?.sale && {
                  productSale: seq?.productSale
                }),
                ...(seq?.badges?.length && {
                  badges: [
                    {
                      badgeId: seq.badges[0]?.badge?.badgeId,
                      publisherBadgeId: seq.badges[0]?.badge?.publisherBadgeId
                    }
                  ]
                })
              };
            })
          : []
      };
      submitOffer(newOffer, rollingOfferId);
    },
    [chosenSegment, productsSequence, submitOffer, rollingOfferId]
  );

  const updateContent = useCallback(async (): Promise<void> => {
    if (!hasMissingProductDetails) {
      await handleSubmit(onSubmit)();
    } else {
      if (edit && watch('active')) {
        setIsUpdateDialogOpen(true);
      } else {
        setIsSaveDialogOpen(true);
      }
    }
  }, [hasMissingProductDetails, handleSubmit, onSubmit, edit, watch]);

  const handleFormSubmit = useCallback((): void => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const formTitle = useMemo((): string => {
    const offerName = getOffer.data?.result?.name;
    if (edit && offerName) {
      return `Edit Rolling Offer (${offerName})`;
    }
    if (edit) {
      return 'Edit Rolling Offer';
    }
    return 'Create Rolling Offer';
  }, [edit, getOffer.data?.result?.name]);

  const areAllBarPointsValid = useMemo((): boolean => {
    return productsSequence.every(
      (_, index) => barPointsValidity[index] !== false
    );
  }, [productsSequence, barPointsValidity]);

  const scheduleValues = watch('schedule');
  const isFormValid = useMemo((): boolean => {
    return (
      !isLoading &&
      formState.isValid &&
      canUserEdit &&
      areAllBarPointsValid &&
      (scheduleValues?.permanent || (scheduleValues?.timeFrames?.length ?? 0) > 0) &&
      productsSequence.length >= MIN_ROLLING_SUB_OFFERS &&
      (!edit || formState.isDirty)
    );
  }, [
    isLoading,
    formState.isValid,
    formState.isDirty,
    canUserEdit,
    areAllBarPointsValid,
    scheduleValues?.permanent,
    scheduleValues?.timeFrames?.length,
    productsSequence.length,
    edit
  ]);

  const handleClose = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  const isImagesReady = !getImages.isLoading && !!getImages.data;

  return (
    <OfferFormLayout
      title={formTitle}
      isFormValid={isFormValid}
      onSave={updateContent}
      onClose={handleClose}
      previewContent={
        <RollingOfferPreview
          currentOfferUI={currentOfferUI}
          productsSequence={productsSequence}
        />
      }
      extraContent={
        <RollingOfferModals
          isSaveDialogOpen={isSaveDialogOpen}
          isUpdateDialogOpen={isUpdateDialogOpen}
          setIsSaveDialogOpen={setIsSaveDialogOpen}
          setIsUpdateDialogOpen={setIsUpdateDialogOpen}
          submitForm={handleFormSubmit}
        />
      }
    >
      {isImagesReady && (
        <FormProvider {...form}>
          <Tabs
            className="h-full pt-10 pb-10 w-[528px]"
            value={tab}
            onValueChange={handleTabChange}
          >
            <FormSectionsTabs
              currentTab={tab}
              tabs={[
                {
                  value: ERollingOfferFormState.GENERAL,
                  label: 'Settings',
                  disabled: getOffer.isLoading && edit
                },
                {
                  value: ERollingOfferFormState.CONFIGURATION,
                  label: 'Configuration',
                  disabled: getOffer.isLoading && edit
                },
                {
                  value: ERollingOfferFormState.SCHEDULE,
                  label: 'Schedule',
                  disabled: getOffer.isLoading && edit
                }
              ]}
            />
            <div className="flex-1 flex justify-center">
              <TabsContent
                value={ERollingOfferFormState.GENERAL}
                className="mt-9 w-full"
              >
                <SettingsTab
                  rollingOfferId={rollingOfferId}
                  edit={edit}
                  offerDesignOptions={offerDesignOptions}
                  chosenSegment={chosenSegment}
                  setChosenSegment={setChosenSegment}
                  data={data}
                  productsSequence={productsSequence}
                  setProductsSequence={setProductsSequence}
                  dup={dup}
                />
              </TabsContent>
              <TabsContent
                value={ERollingOfferFormState.CONFIGURATION}
                className="mt-9 w-full"
              >
                <ConfigurationTab
                  rollingOfferId={rollingOfferId}
                  productsSequence={productsSequence}
                  setProductsSequence={setProductsSequence}
                  setBarPointsValidity={setBarPointsValidity}
                  relevantBadgeType={relevantBadgeType}
                />
              </TabsContent>
              <TabsContent
                value={ERollingOfferFormState.SCHEDULE}
                className="mt-9 w-full"
              >
                <ScheduleTab
                  rollingOfferId={rollingOfferId}
                  edit={edit}
                />
              </TabsContent>
            </div>
          </Tabs>
        </FormProvider>
      )}
    </OfferFormLayout>
  );
};

export default RollingOfferForm;
